import { CharacterDefinition } from '@hexcrawl/shared-utils';
import { ArtManifest } from './types.ts';

export interface ResolvedLayer {
  imageUrl: string;
  tint?: string;
}

export function resolveLayerFiles(
  animFolder: string,
  definition: CharacterDefinition,
  manifest: ArtManifest,
): ResolvedLayer[] {
  const animData = manifest.animations[animFolder];
  if (!animData) throw new Error(`Animation folder "${animFolder}" not found in manifest.`);

  const result: ResolvedLayer[] = [];
  const sortedKeys = Object.keys(definition.layers).sort((a, b) => Number(a) - Number(b));

  for (const layerKey of sortedKeys) {
    const selection = definition.layers[layerKey];
    if (selection === null) continue;

    const layerIndex = Number(layerKey);
    const { part, variant, tint } = selection;

    const layerData = animData.layers[layerKey];
    if (!layerData) {
      console.warn(`[SpriteAssembly] Layer ${layerKey} not found in ${animFolder} — skipping.`);
      continue;
    }

    const partData = layerData.parts[part];
    if (!partData) {
      console.warn(`[SpriteAssembly] Part "${part}" not found on layer ${layerKey} in ${animFolder} — skipping.`);
      continue;
    }

    let resolvedVariant: string;

    if (partData.tintable) {
      if (partData.variants.includes('Blank')) {
        resolvedVariant = 'Blank';
      } else {
        const fallback = [...(partData.unusedVariants ?? [])].sort()[0];
        if (!fallback) {
          console.warn(`[SpriteAssembly] No variants for tintable "${part}" on layer ${layerKey} in ${animFolder} — skipping.`);
          continue;
        }
        console.warn(`[SpriteAssembly] Gap: Blank missing for ${animFolder}/layer${layerKey}/${part}, falling back to "${fallback}".`);
        resolvedVariant = fallback;
      }
    } else {
      if (!variant) {
        resolvedVariant = partData.variants[0];
        console.warn(`[SpriteAssembly] No variant for "${part}" on layer ${layerKey} — falling back to "${resolvedVariant}".`);
      } else if (partData.variants.includes(variant)) {
        resolvedVariant = variant;
      } else {
        resolvedVariant = partData.variants[0];
        console.warn(`[SpriteAssembly] Variant "${variant}" not found for "${part}" in ${animFolder} — falling back to "${resolvedVariant}".`);
      }
    }

    const token = layerIndex < 0 ? `NegativeLayer${Math.abs(layerIndex)}` : `Layer${layerIndex}`;
    result.push({
      imageUrl: `/assets/character/${animFolder}/${token}_${part}_${resolvedVariant}.png`,
      tint,
    });
  }

  return result;
}
